import type { ApiError } from "@circle-react/config/CustomErrors";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { chatRoomParticipantApi } from "@/react/api/chatRoomParticipantApi";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

export const useParticipantActions = ({
  refetchChatParticipants,
  refetchChatRoom,
  uuid,
}: {
  refetchChatParticipants: () => void;
  refetchChatRoom: () => void;
  uuid: string;
}) => {
  const toastr = useToast();

  const { mutateAsync: handleMakeAdmin } = useMutation<any, ApiError, any>({
    mutationFn: ({ participantId, status }) =>
      chatRoomParticipantApi.update({
        uuid,
        id: participantId,
        admin: status,
      }),
    onSuccess: ({
      chat_room_participant: {
        name,
        roles: { admin },
      },
    }) => {
      const displayName = name || t("messaging.member"); //Fallback incase name not is set.
      refetchChatParticipants();
      refetchChatRoom();
      toastr.success(
        admin
          ? t("messaging.member_made_admin", { name: displayName })
          : t("messaging.member_removed_as_admin", {
              name: displayName,
            }),
        {
          duration: "short",
          shouldUseProgress: false,
        },
      );
    },
    onError: () => {
      toastr.error(t("request_failure_message"));
    },
  });

  const { mutateAsync: removeParticipant } = useMutation<any, ApiError, any>({
    mutationFn: ({ participantId }) =>
      chatRoomParticipantApi.destroy({
        uuid,
        id: participantId,
      }),
    onSuccess: ({ message }) => {
      refetchChatParticipants();
      refetchChatRoom();
      toastr.success(message);
    },
    onError: () => {
      toastr.error(t("request_failure_message"));
    },
  });

  return {
    handleMakeAdmin,
    removeParticipant,
  };
};
